import { Collapse } from '@mui/material'
import { RegisterItem } from '@core/entities/register/RegisterItem'
import { RegisterItemInspectionProperty } from '@core/entities/register/RegisterItem/types'
import { ScheduledInspection } from '@core/entities/register/RegisterInspection/ScheduledInspection'
import type { InspectionDetailsClickParams } from './RegisterScheduleInspectionsCard'
import { RegisterScheduleInspectionsCard } from './RegisterScheduleInspectionsCard'
import { localizedStrings } from '@core/strings'
import { Box, Flex, Text } from '@fundamentals'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useMemo, useState } from 'react'
import { RegisterInspectionCard } from './RegisterInspectionCard'
import { useDeepCompareMemo } from 'use-deep-compare'

type InactiveScheduleInspectionRow = {
  inspectionConfig: RegisterItemInspectionProperty<string>
  inspection: ScheduledInspection
}

interface RegisterScheduleInspectionsTabProps {
  organisationId: number
  registerId: number
  registerItemId: number
  registerItem: RegisterItem
  scheduleInspectionConfigs: Array<RegisterItemInspectionProperty<string>>
  onInspectionDetailsClick: (params: InspectionDetailsClickParams) => void
}

export const RegisterScheduleInspectionsTab = ({
  organisationId,
  registerId,
  registerItemId,
  registerItem,
  scheduleInspectionConfigs,
  onInspectionDetailsClick,
}: RegisterScheduleInspectionsTabProps) => {
  const projectId = registerItem.register?.projectId
  const [inactiveSectionExpanded, setInactiveSectionExpanded] = useState(true)

  const allInspections = useDeepCompareMemo(
    () => scheduleInspectionConfigs,
    [scheduleInspectionConfigs],
  )

  const inactiveRows = useMemo((): InactiveScheduleInspectionRow[] => {
    const rows: InactiveScheduleInspectionRow[] = []
    for (const inspectionConfig of allInspections) {
      if (inspectionConfig.inspection) continue
      for (const inspection of inspectionConfig.scheduledInspections ?? []) {
        if (
          inspection.scheduleConfig &&
          inspection.scheduleConfig.isActive === false
        ) {
          rows.push({ inspectionConfig, inspection })
        }
      }
    }
    return rows
  }, [allInspections])

  return (
    <Flex flexDirection='column' gap={2} sx={{ py: 2 }}>
      {allInspections.flatMap((inspectionConfig) => {
        if (inspectionConfig.inspection) {
          return (
            <RegisterInspectionCard
              key={inspectionConfig.registerPropertyId}
              organisationId={organisationId}
              registerId={registerId}
              registerItemId={registerItemId}
              projectId={projectId}
              inspectionConfig={inspectionConfig}
              onInspectionDetailsClick={onInspectionDetailsClick}
            />
          )
        }

        return (inspectionConfig.scheduledInspections ?? [])
          .filter(
            (scheduledInspection) =>
              scheduledInspection.scheduleConfig?.isActive !== false,
          )
          .map((scheduledInspectionRaw) => {
            const scheduledInspection = ScheduledInspection.new(
              scheduledInspectionRaw,
            )
            return (
              <RegisterScheduleInspectionsCard
                key={`${inspectionConfig.registerPropertyId}-${scheduledInspection.id}`}
                organisationId={organisationId}
                registerId={registerId}
                registerItemId={registerItemId}
                projectId={projectId}
                inspectionConfig={inspectionConfig}
                scheduledInspection={scheduledInspection}
                onInspectionDetailsClick={onInspectionDetailsClick}
              />
            )
          })
      })}
      {inactiveRows.length > 0 ? (
        <Flex
          flexDirection='column'
          gap={1.5}
          sx={{ width: '100%' }}
          data-test='inactive-scheduled-inspections'
        >
          <Flex
            alignItems='center'
            justifyContent='space-between'
            onClick={() => setInactiveSectionExpanded((e) => !e)}
            sx={{
              cursor: 'pointer',
              userSelect: 'none',
              py: 0.5,
            }}
          >
            <Text variant='body2' color='text.secondary' fontWeight={600}>
              {localizedStrings.inactiveScheduleInspections}
            </Text>
            <ExpandMore
              sx={{
                color: 'text.secondary',
                transform: inactiveSectionExpanded
                  ? 'rotate(0deg)'
                  : 'rotate(180deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </Flex>
          <Collapse in={inactiveSectionExpanded}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}
            >
              {inactiveRows.map(({ inspectionConfig, inspection }) => {
                const scheduledInspection = ScheduledInspection.new(inspection)
                return (
                  <RegisterScheduleInspectionsCard
                    key={`${inspectionConfig.registerPropertyId}-${scheduledInspection.id}-inactive`}
                    organisationId={organisationId}
                    registerId={registerId}
                    registerItemId={registerItemId}
                    projectId={projectId}
                    inspectionConfig={inspectionConfig}
                    scheduledInspection={scheduledInspection}
                    isInactive
                    onInspectionDetailsClick={onInspectionDetailsClick}
                  />
                )
              })}
            </Box>
          </Collapse>
        </Flex>
      ) : null}
    </Flex>
  )
}
